import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { retryExchange, RetryExchangeOptions } from '@urql/exchange-retry';
import {
  cacheExchange,
  Client as UrqlClient,
  Exchange,
  fetchExchange,
  makeOperation,
  mapExchange,
  Operation,
  Provider as UrqlProvider,
} from 'urql';

type GetHeaders = (operation: Operation) => Promise<Record<string, string>>;
/**
 * asyncHeaderExchange - A urql exchange that adds the headers from promise value.
 *
 * @param getHeaders {GetHeaders} - A function that returns a promise that resolves to a headers object.
 */
export const asyncHeaderExchange = (getHeaders: GetHeaders): Exchange =>
  mapExchange({
    async onOperation(operation) {
      if (typeof operation.context.fetchOptions === 'function') {
        operation.context.fetchOptions = operation.context.fetchOptions();
      }
      if (!operation.context.fetchOptions) {
        operation.context.fetchOptions = {
          headers: {},
        };
      }
      const headers = await getHeaders(operation);
      return makeOperation(operation.kind, operation, {
        ...operation.context,
        fetchOptions: {
          ...operation.context.fetchOptions,
          headers: {
            ...operation.context.fetchOptions.headers,
            ...headers,
          },
        },
      });
    },
  });

const generateUrqlClient = async (token: string, uri: string, source: string) => {
  const retryOptions: RetryExchangeOptions = {
    initialDelayMs: 1000,
    maxDelayMs: 15000,
    randomDelay: true,
    maxNumberAttempts: 3,
    retryIf: (err) => !!err?.networkError,
  };

  return new UrqlClient({
    url: `${uri}/graphql`,
    exchanges: [cacheExchange, retryExchange(retryOptions), fetchExchange],
    fetchOptions: () => {
      const fetchOptions: Record<string, any> = {
        headers: {
          Accept: '*/*', // This is critical to ensure that codex API returns a gzipped response.
          authorization: `Bearer ${token}`,
          'x-dbt-partner-source': source,
        },
      };
      return fetchOptions;
    },
  });
};

type ClientState = {
  graphqlUri: string | undefined;
  token: string | undefined;
  resetClient: () => void;
  source: string | undefined;
};

const ClientContext = createContext<ClientState>({
  graphqlUri: undefined,
  token: undefined,
  resetClient: () => {},
  source: undefined,
});

interface DiscoveryUrqlUriProviderProps {
  token: string;
  uri: string;
  source: string;
}

export const useDiscoveryContext = () => {
  return useContext(ClientContext);
};

/**
 * Ensures trailing `/`, `/beta`, or `/internal`.
 * It then replaces trailing `/`, `/beta`, or `/internal` with `/`
 */
const useBaseUri = (uri: string) => {
  /**
   * cloud.metadata.getdbt.com
   * cloud.metadata.getdbt.com/
   * cloud.metadata.getdbt.com/beta
   * cloud.metadata.getdbt.com/internal
   *
   * becomes
   *
   * cloud.metadata.getdbt.com/
   */
  const externalBaseUrl = uri.replace(/((?:\/beta|\/internal|\/)?\/?$)/i, '');
  return `${externalBaseUrl}`;
};

const DiscoveryUrqlUriProvider: React.FC<
  PropsWithChildren<DiscoveryUrqlUriProviderProps>
> = ({ token, uri, source, children }) => {
  const externalBaseUrl = useBaseUri(uri);

  const [client, setClient] = useState<UrqlClient>();

  const resetClient = useCallback(() => {
    generateUrqlClient(token, externalBaseUrl, source).then(setClient);
  }, [externalBaseUrl, source, token]);

  useEffect(() => {
    resetClient();
  }, [resetClient]);

  if (!client) return null;

  return (
    <ClientContext.Provider
      value={{
        graphqlUri: externalBaseUrl,
        token,
        resetClient,
        source,
      }}
    >
      <UrqlProvider value={client}>{children}</UrqlProvider>
    </ClientContext.Provider>
  );
};

interface DiscoveryUrqlProviderProps extends PropsWithChildren<{}> {
  token: string | undefined;
  uri: string | undefined;
  source: string | undefined;
}

const DiscoveryUrqlProvider: React.FC<DiscoveryUrqlProviderProps> = ({
  token,
  uri,
  source,
  children,
}) => {
  if (!token || !uri || !source) return null;
  return (
    <DiscoveryUrqlUriProvider token={token} uri={uri} source={source}>
      {children}
    </DiscoveryUrqlUriProvider>
  );
};

export const useDiscoveryUrqlClient = () => useContext(ClientContext);

export default DiscoveryUrqlProvider;
